// Who's playing? The signed-in player on this device. Device-only: never part of the cloud save.
import { sanitiseName, nameAllowed, validMMYY } from './codes.js';

export const IDENTITY_KEY = 'saicraft.identity.v1';
const str = (v) => (typeof v === 'string' && v ? v : null);

// Private mode or blocked storage can throw on access: then nothing is remembered.
function browserStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch (e) {
    return null;
  }
}

function clean(d) {
  if (!d || typeof d !== 'object' || !str(d.playerId)) return null;
  return {
    v: 1,
    playerId: d.playerId,
    token: str(d.token),
    exp: Number.isFinite(d.exp) ? d.exp : 0,
    name: typeof d.name === 'string' ? d.name : '',
    cloudCode: str(d.cloudCode),
    roomCode: str(d.roomCode),
  };
}

export class Identity {
  constructor() {
    this.current = null;
    this.storage = undefined;
  }

  load(storage = browserStorage()) {
    this.storage = storage;
    try {
      const raw = storage?.getItem(IDENTITY_KEY);
      this.current = raw ? clean(JSON.parse(raw)) : null;
    } catch (e) {
      this.current = null;
    }
    return this.current;
  }

  save(data) {
    this.current = clean(data);
    if (this.storage === undefined) this.storage = browserStorage();
    try {
      if (this.current) this.storage?.setItem(IDENTITY_KEY, JSON.stringify(this.current));
      else this.storage?.removeItem(IDENTITY_KEY);
    } catch (e) {
      /* storage full / private mode */
    }
    return this.current;
  }

  clear() {
    this.save(null);
  }

  get data() {
    return this.current;
  }

  get loggedIn() {
    return !!this.current;
  }

  tokenValid(nowSeconds = Date.now() / 1000) {
    return !!this.current?.token && this.current.exp > nowSeconds;
  }

  // A refreshed token from a room snapshot; setToken(null, 0) forgets a rejected one.
  setToken(token, exp) {
    if (!this.current) return false;
    this.save({ ...this.current, token: str(token), exp: Number(exp) || 0 });
    return true;
  }

  async login({ name, mmyy, claimCode, accept } = {}, { fetchImpl = globalThis.fetch, timeout = 6000 } = {}) {
    const nick = sanitiseName(name);
    if (!nameAllowed(nick)) return { ok: false, error: 'name' };
    if (!validMMYY(mmyy)) return { ok: false, error: 'mmyy' };
    if (typeof fetchImpl !== 'function') return { ok: false, error: 'offline' };
    const body = { name: nick, mmyy: String(mmyy) };
    if (claimCode) body.claimCode = String(claimCode);
    if (accept) body.accept = true;
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    let timer;
    const late = new Promise((resolve) => {
      timer = setTimeout(() => {
        ctrl?.abort();
        resolve(null);
      }, timeout);
    });
    const ask = async () => {
      const res = await fetchImpl('/api/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        cache: 'no-store',
        signal: ctrl?.signal,
      });
      let json = null;
      try {
        json = await res.json();
      } catch (e) {
        /* not JSON */
      }
      return { status: res.status, json: json && typeof json === 'object' ? json : {} };
    };
    let reply;
    try {
      reply = await Promise.race([ask(), late]);
    } catch (e) {
      reply = null;
    } finally {
      clearTimeout(timer);
    }
    if (!reply) return { ok: false, error: 'offline' };
    const { status, json } = reply;
    if (status === 200 && json.ok && str(json.playerId) && str(json.token)) {
      const data = this.save({ ...json, name: str(json.name) || nick });
      return { ok: true, data, created: !!json.created, linked: !!json.linked };
    }
    if (status === 409) {
      const out = { ok: false, error: 'conflict' };
      if (json.reason) out.reason = json.reason;
      if (json.name) out.name = json.name;
      return out;
    }
    if (status === 429) return { ok: false, error: 'slow' };
    if (status === 400 && (json.error === 'name' || json.error === 'mmyy')) return { ok: false, error: json.error };
    return { ok: false, error: 'server' };
  }
}

export const identity = new Identity();
